// ============================================================
// useImportExport - Session export/import handlers
// ============================================================
// Two-step flow with selection dialogs:
//   - doExport: opens ExportDialog → user selects sections → download
//   - doImport: reads file → normalizes → opens ImportDialog → apply
//
// The hook owns the dialogs, the file picker and the sinks; what goes into
// the file and what comes out of it is utils/sessionTransfer.
// ============================================================
import { useState, useCallback, useMemo, useRef } from 'react';
import {
  characterFileProblem,
  isCharacterFile,
  namedForTable,
  readCharacterFile,
} from '../engine/roster/characterFile';
import { sessionInstances } from '../engine/roster/naming';
import { infoLine } from './shellCombatant';
import useJsonFileDrop from './useJsonFileDrop';
import downloadJson from '../utils/downloadJson';
import { sessionExport, readSessionText, applySessionImport, importWarningEntry } from '../utils/sessionTransfer';

/**
 * @param {Object} params
 * @param {Array}    params.tabs
 * @param {Function} params.setTabs
 * @param {string}   params.activeTab
 * @param {Function} params.setActiveTab
 * @param {Object}   params.atkProfiles
 * @param {Function} params.importAtkProfiles
 * @param {Object}   params.tgtTemplates
 * @param {Function} params.setTgtTemplates
 * @param {Function} params.syncAllTabsToFirestore
 * @param {Function} params.addNarrative
 * @param {Function} params.setPendingAttack
 * @param {Function} params.clearAllPendingDefences
 * @param {Array}    params.roster            - v4 export source
 * @param {Function} params.importRoster      - v4 import sink
 * @param {Function} params.importCharacters  - a character file's people join the roster
 * @param {Array}    params.loadouts
 * @param {Function} params.importLoadouts
 * @param {Object}   params.terrain           - the battlefield, the map library and
 * @param {Array}    params.mapLibrary          where everyone stands
 * @param {Object}   params.tokens
 * @param {Array}    params.waitingRooms      - the GM's staged combatants; never written unless asked for
 * @param {Array}    params.handouts          - metadata, notes and links - never the files (v8.363.10)
 * @param {Function} params.importHandouts
 * @param {Function} params.importBattlefield
 */
export default function useImportExport(params) {
  const { doExport, showExportDialog, handleExportConfirm, handleExportCancel } = useSessionExport(params);
  const { doImport, importDialogData, handleImportConfirm, handleImportCancel } = useSessionImport(params);
  return {
    doExport,
    doImport,
    // Export dialog
    showExportDialog,
    handleExportConfirm,
    handleExportCancel,
    // Import dialog
    importDialogData,
    handleImportConfirm,
    handleImportCancel,
  };
}

/** The export dialog, and the download its confirm starts. */
function useSessionExport({
  tabs,
  activeTab,
  atkProfiles,
  tgtTemplates,
  roster = [],
  loadouts = [],
  terrain = null,
  mapLibrary = [],
  tokens = {},
  waitingRooms = [],
  handouts = [],
}) {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const doExport = useCallback(() => setShowExportDialog(true), []);
  const handleExportCancel = useCallback(() => setShowExportDialog(false), []);
  const handleExportConfirm = useCallback(
    (selections) => {
      const src = { tabs, activeTab, atkProfiles, tgtTemplates, roster, loadouts, terrain, mapLibrary, tokens };
      const now = new Date().toISOString();
      const data = sessionExport(selections, { ...src, waitingRooms, handouts }, now);
      downloadJson(data, 'deathwatch-session-' + now.slice(0, 10) + '.json');
      setShowExportDialog(false);
    },
    [atkProfiles, tabs, activeTab, tgtTemplates, roster, loadouts, terrain, mapLibrary, tokens, waitingRooms, handouts],
  );
  return { doExport, showExportDialog, handleExportConfirm, handleExportCancel };
}

/**
 * Read the picked file as text, then clear the input so picking the same
 * file again still fires a change.
 */
function readPickedFile(e, onText) {
  const file = e.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => onText(ev.target.result);
  reader.readAsText(file);
  e.target.value = '';
}

/**
 * When `text` is a character file, hand its combatants to `importCharacters`
 * under the names the table will know them by, and return the line to log;
 * null when it is anything else.
 */
function joinCharacterFile(text, { importCharacters, roster, waitingRooms }) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return null; // not JSON at all: the session reader says so properly
  }
  if (!isCharacterFile(data) || !importCharacters) return null;
  const problem = characterFileProblem(data);
  if (problem) return `Import failed: ${problem}.`;
  const read = readCharacterFile(data);
  const combatants = namedForTable(read.combatants, sessionInstances(roster ?? [], waitingRooms ?? []));
  importCharacters(combatants);
  const unread = read.skipped ? ` ${read.skipped} could not be read.` : '';
  return combatants.length
    ? `📥 Joined from a character file: ${combatants.map((c) => c.name).join(', ')}.${unread}`
    : `That character file holds nobody.${unread}`;
}

/** The file picker, the import dialog, and the sinks its confirm feeds. */
function useSessionImport(params) {
  const { activeTab, addNarrative, importCharacters, roster, waitingRooms } = params;
  const [importDialogData, setImportDialogData] = useState(null);
  // The parsed, normalized file, held for the confirm step.
  const importRawRef = useRef(null);
  const receive = useCallback(
    (text) => {
      const say = (line) => addNarrative(activeTab, infoLine(line));
      // A character file is not a session: its people join the roster, with no dialog.
      const joined = joinCharacterFile(text, { importCharacters, roster, waitingRooms });
      if (joined) return say(joined);
      const read = readSessionText(text);
      if (read.error) return say(read.error);
      importRawRef.current = read.raw;
      setImportDialogData(read.dialog);
    },
    [activeTab, addNarrative, importCharacters, roster, waitingRooms],
  );
  const doImport = useCallback((e) => readPickedFile(e, receive), [receive]);
  // The same road for a file dropped on the page.
  useJsonFileDrop(receive);
  const close = useCallback(() => {
    setImportDialogData(null);
    importRawRef.current = null;
  }, []);
  const handleImportConfirm = useImportConfirm({ ...params, importRawRef, importDialogData, close });
  return { doImport, importDialogData, handleImportConfirm, handleImportCancel: close };
}

/**
 * Apply the selected sections, clear anything pending (the imported
 * session is a different fight), log what normalization fixed, close.
 */
function useImportConfirm(p) {
  const { importRawRef, importDialogData, activeTab, addNarrative, setPendingAttack, clearAllPendingDefences, close } =
    p;
  const sinks = useImportSinks(p);
  return useCallback(
    (selections) => {
      const raw = importRawRef.current;
      if (!raw) return;
      applySessionImport(raw, selections, sinks);
      setPendingAttack(null);
      if (clearAllPendingDefences) clearAllPendingDefences();
      const warning = importWarningEntry(raw, importDialogData?.warnings || [], activeTab);
      if (warning) addNarrative(warning.tabId, { type: 'info', text: warning.text, timestamp: Date.now() });
      close();
    },
    [importRawRef, importDialogData, activeTab, sinks, addNarrative, setPendingAttack, clearAllPendingDefences, close],
  );
}

/** Where each imported section goes - one object, rebuilt only when a sink changes. */
function useImportSinks(p) {
  const { setTabs, setActiveTab, syncAllTabsToFirestore, importAtkProfiles, setTgtTemplates } = p;
  const { importRoster, importLoadouts, importBattlefield, importHandouts = null } = p;
  return useMemo(
    () => ({
      setTabs,
      setActiveTab,
      syncAllTabsToFirestore,
      importAtkProfiles,
      setTgtTemplates,
      importRoster,
      importLoadouts,
      importBattlefield,
      importHandouts,
    }),
    [
      setTabs,
      setActiveTab,
      syncAllTabsToFirestore,
      importAtkProfiles,
      setTgtTemplates,
      importRoster,
      importLoadouts,
      importBattlefield,
      importHandouts,
    ],
  );
}
